import type { GridColDef } from '@mui/x-data-grid'
import { isTimberLikeSettings } from '@editor/pages/Results2/components/Results2DetailPane/utils'
import { CheckSettingsRow, MultiEditCheckSettingsElement } from '../MultiEditCheckSettings/types'

/**
 * Both flags default to off on the backend, so a member that predates the
 * setting is normalized to the same shape the fields expect.
 */
const SHEAR_CHECK_SETTINGS_DEFAULT: ShearCheckSettings = {
  reduce_point_loads_close_to_supports: false,
  reduce_shear_force: false,
}

/**
 * True when the member check settings carry shear check settings, i.e. the
 * member is timber or timber-slab. Steel columns and "other" members do not.
 */
export const hasShearCheckSettings = (
  settings: SettingsOnMember | undefined,
): settings is TimberCheckSettings | TimberSlabCheckSettings => isTimberLikeSettings(settings)

export const getShearCheckSettings = (
  settings: SettingsOnMember | undefined,
): ShearCheckSettings | undefined =>
  settings && 'shear_check_settings' in settings ? settings.shear_check_settings : undefined

/**
 * Ensure the shared settings object always carries a shear_check_settings object
 * so the field component never reads it as null/undefined.
 */
export const normalizeShearCheckSettings = (settings: SettingsOnMember): SettingsOnMember =>
  ({
    ...settings,
    shear_check_settings: getShearCheckSettings(settings) || SHEAR_CHECK_SETTINGS_DEFAULT,
  } as SettingsOnMember)

/**
 * Applies the staged shared shear check settings to one member, leaving every
 * other part of its check settings untouched.
 */
export const mergeShearCheckSettings = (
  existing: SettingsOnMember,
  shared: SettingsOnMember,
): SettingsOnMember =>
  ({
    ...existing,
    shear_check_settings: getShearCheckSettings(shared) || SHEAR_CHECK_SETTINGS_DEFAULT,
  } as SettingsOnMember)

export const shearCheckSettingsColumns: GridColDef<CheckSettingsRow>[] = [
  { field: 'reducePointLoads', headerName: 'Punktlasten abmindern', flex: 1, minWidth: 100 },
  { field: 'reduceShearForce', headerName: 'V im Abstand h', flex: 1, minWidth: 100 },
]

/** While a shared value is staged it is previewed for every row. */
export const buildShearCheckSettingsRow = (
  element: MultiEditCheckSettingsElement,
  sharedSettings: SettingsOnMember | undefined,
): Record<string, string> => {
  const settings =
    getShearCheckSettings(sharedSettings) ?? getShearCheckSettings(element.checkSettings)

  return {
    reducePointLoads: settings?.reduce_point_loads_close_to_supports ? 'Ja' : 'Nein',
    reduceShearForce: settings?.reduce_shear_force ? 'Ja' : 'Nein',
  }
}
